import React from 'react';
import styled from 'styled-components';
import { useBetty } from '../../context/BettyContext';
import { useTextToSpeech } from '../../hooks/useTextToSpeech';
import { useAudio } from '../../context/AudioContext';
// import { BettyTrackCard } from './BettyTrackCard'; // Unused for now

interface Track {
  id: string;
  title: string;
  artist: string;
  url: string;
  album_id?: string;
  album_title?: string;
  year?: number;
  genre: string[];
  description?: string;
  mood_tags: string[];
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  tracks?: Track[];
  isLoading?: boolean;
}

interface Props {
  message: ChatMessage;
}

const MessageWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isUser'
})<{ isUser: boolean }>`
  display: flex;
  justify-content: ${props => props.isUser ? 'flex-end' : 'flex-start'};
  margin-bottom: ${props => props.isUser ? '8px' : '16px'};
  width: 100%;
`;

const MessageBubble = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isUser' && prop !== 'isLoading'
})<{ isUser: boolean; isLoading?: boolean }>`
  ${props => props.isUser ? 'max-width: 70%;' : 'width: 100%;'}
  padding: 16px 20px;
  border-radius: ${props => props.isUser ? '18px 18px 4px 18px' : '4px'};
  background: ${props => {
    if (props.isLoading) return 'rgba(255, 182, 193, 0.2)';
    return props.isUser 
      ? 'linear-gradient(135deg, #ff69b4, #ffb6c1)' 
      : 'rgba(255, 255, 255, 0.9)';
  }};
  color: ${props => props.isUser ? 'white' : '#8b4513'};
  border: 1px solid ${props => {
    if (props.isLoading) return 'rgba(255, 182, 193, 0.4)';
    return props.isUser ? 'transparent' : 'rgba(255, 182, 193, 0.3)';
  }};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  animation: ${props => props.isLoading ? 'pulse 1.5s infinite' : 'none'};
  margin-left: ${props => props.isUser ? 'auto' : '0'};
  backdrop-filter: blur(10px);
  
  @keyframes pulse {
    0% { opacity: 0.6; }
    50% { opacity: 1; }
    100% { opacity: 0.6; }
  }

`;

const MessageMeta = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isUser'
})<{ isUser: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: ${props => props.isUser ? '4px' : '8px'};
  font-size: 0.85rem;
  opacity: 0.8;
  justify-content: ${props => props.isUser ? 'flex-end' : 'flex-start'};
`;

const Avatar = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isUser'
})<{ isUser: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.isUser 
    ? 'linear-gradient(135deg, #8b4513, #a0522d)' 
    : 'linear-gradient(135deg, #ff69b4, #ffb6c1)'
  };
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  margin: ${props => props.isUser ? '0 0 0 8px' : '0 8px 0 0'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  color: ${props => props.isUser ? 'white' : 'white'};
  font-weight: bold;
`;

const MessageContent = styled.div`
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const TracksContainer = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TracksHeader = styled.div`
  font-size: 0.9rem;
  opacity: 0.8;
  margin-bottom: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(244, 228, 193, 0.2);
`;

const VoiceControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(244, 228, 193, 0.2);
`;

const VoiceButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'isPlaying' && prop !== 'isSynthesizing'
})<{ isPlaying: boolean; isSynthesizing: boolean }>`
  background: ${props => {
    if (props.isSynthesizing) return 'rgba(255, 105, 180, 0.3)';
    if (props.isPlaying) return 'linear-gradient(135deg, #ff4757, #ff6b6b)';
    return 'linear-gradient(135deg, #ff69b4, #ffb6c1)';
  }};
  border: none;
  border-radius: 20px;
  padding: 6px 12px;
  color: white;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(255, 105, 180, 0.3);
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const SpeakingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: rgba(139, 69, 19, 0.7);
  font-size: 0.8rem;
  
  .dots {
    display: flex;
    gap: 2px;
  }
  
  .dot {
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: #ff69b4;
    animation: speakingDots 1.4s infinite ease-in-out;
    
    &:nth-child(1) { animation-delay: 0s; }
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
  
  @keyframes speakingDots {
    0%, 60%, 100% {
      transform: scale(1);
      opacity: 0.5;
    }
    30% {
      transform: scale(1.2);
      opacity: 1;
    }
  }
`;

const formatTime = (date: Date) => {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export const BettyChatMessage: React.FC<Props> = ({ message }) => {
  const { openSidebar } = useBetty();
  const { isPlaying, isSynthesizing, playText, stopPlayback } = useTextToSpeech();
  const { pause } = useAudio(); // Import audio context to stop music
  const isUser = message.role === 'user';

  // Manual TTS playback only when button is clicked

  const handleMessageClick = () => {
    if (!isUser && message.tracks && message.tracks.length > 0) {
      openSidebar(message.tracks, `Previous search results`);
    }
  };

  const handleVoiceToggle = () => {
    if (isPlaying) {
      stopPlayback();
    } else {
      // Stop any playing music before starting TTS
      pause();
      playText(message.content);
    }
  };

  return (
    <MessageWrapper isUser={isUser}>
      {!isUser && (
        <Avatar isUser={false}>
          🎵
        </Avatar>
      )}
      
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <MessageMeta isUser={isUser}>
          <span>{isUser ? 'You' : 'Betty'}</span>
          <span>•</span>
          <span>{formatTime(message.timestamp)}</span>
        </MessageMeta>
        
        <MessageBubble 
          isUser={isUser} 
          isLoading={message.isLoading}
          onClick={handleMessageClick}
          style={{ cursor: (!isUser && message.tracks && message.tracks.length > 0) ? 'pointer' : 'default' }}
        >
          <MessageContent>
            {message.content}
          </MessageContent>
          
          {message.tracks && message.tracks.length > 0 && (
            <TracksContainer>
              <TracksHeader>
                🎶 Click to view {message.tracks.length} track{message.tracks.length > 1 ? 's' : ''} →
              </TracksHeader>
            </TracksContainer>
          )}
          
          {/* Voice controls for assistant messages */}
          {!isUser && message.content && !message.isLoading && (
            <VoiceControls>
              <VoiceButton
                onClick={handleVoiceToggle}
                isPlaying={isPlaying}
                isSynthesizing={isSynthesizing}
                disabled={isSynthesizing}
                title={isSynthesizing ? 'Synthesizing speech...' : isPlaying ? 'Stop speaking' : 'Speak this message'}
              >
                {isSynthesizing ? '⏳' : isPlaying ? '⏹' : '🔊'}
                {isSynthesizing ? 'Synthesizing...' : isPlaying ? 'Stop' : 'Speak'}
              </VoiceButton>
              
              {(isPlaying || isSynthesizing) && (
                <SpeakingIndicator>
                  {isSynthesizing ? 'Preparing speech' : 'Speaking'}
                  <div className="dots">
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                  </div>
                </SpeakingIndicator>
              )}
            </VoiceControls>
          )}
        </MessageBubble>
      </div>
      
      {/* Do not render a per-message user avatar; keep the bubble flush right */}
    </MessageWrapper>
  );
};